import { useContext, useEffect, useState, useRef } from 'react';
import { UserContext } from '../context/UserContext';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  CardImageBg,
  Heading,
  HomeTitleWithCTA,
  LayoutGrid,
  CardGeneral,
  EmptyListCTA,
  CardRepair,
  Modal,
  Button,
  VoidBadgeIcon,
} from '@rabbit/elements/shared-components';
import {
  getGreeting,
  useGetConsumerHoldings,
  useGetConsumerCases,
  getAllHoldingProxies,
} from '@rabbit/bizproc/react';
import { BL_Warranty } from '@rabbit/bizproc/core';
import { WarrantyStatus } from '@rabbit/data/types';
import noProductsImg from '../assets/images/service-request-empty.png';
import ROUTE_NAME from '../utils/url-constants';
//import yolo from '../assets/images/yolo.jpg';
import bg_motd from '../assets/images/bg_motd.jpg';
import {
  DTHoldingProxy,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  TableCover,
} from '@rabbit/data/types';
import {
  fixCategoryAndSortHoldingsByTCreate,
  getStoneImages,
} from '../utils/helpers';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { useMyConsumerPersona } from '@rabbit/data/portal';

export function DashboardView() {
  const navigate = useNavigate();
  const consumerPersona = useMyConsumerPersona();
  const consumerPersonaData = consumerPersona?.data.consumer_private;
  const consumerPersonaId = consumerPersona?.personaId;

  const { getAllHoldingProxies, getVendableByHoldingLink } = useGetConsumerHoldings(
    consumerPersonaId || ''
  );
  const { openCaseIds } = useGetConsumerCases() || {};

  const [isLoading, setIsLoading] = useState(true);
  const [userHoldings, setUserHoldings] = useState<DTHoldingProxy[]>([]);
  const [serialRequiredProduct, setSerialRequiredProduct] =
    useState<DTHoldingProxy | null>(null);
  const { t } = useTranslation();
  const { config, tenantInfo } = useContext(AppContext);
  
  // Track the last fetched persona ID to prevent duplicate fetches
  const lastFetchedPersonaId = useRef<string | null>(null);
  let tenantType = 'defaultTenants';
  if (t('tenantLink') === 'NUCOVER') tenantType = 'tableTenants';

  const checkSerialNumberRequired = async (holding: DTHoldingProxy) => {
    if (holding.serial === undefined || holding.serial === '') {
      if (holding.vendable && holding.holdingLink) {
        const vendable = await getVendableByHoldingLink(holding.holdingLink);
        return vendable instanceof Error
          ? config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED
          : vendable?.serialNumberRequired ??
              config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED;
      }
    }
    return false;
  };

  const getCases = () => {
    return openCaseIds?.slice(0, 9)?.map((singleCase) => {
      return (
        <CardRepair
          key={singleCase.case}
          id={singleCase}
          onClick={() => navigate(`${ROUTE_NAME.REPAIRS}/${singleCase.case}`)}
          showSpotlight
          getStoneImages={getStoneImages}
        />
      );
    });
  };

  useEffect(() => {
    // Skip if we've already fetched for this persona ID
    if (consumerPersonaId && lastFetchedPersonaId.current === consumerPersonaId) {
      return;
    }

    // Skip if consumer data isn't ready yet
    if (!consumerPersonaId) {
      setUserHoldings([]);
      setSerialRequiredProduct(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    lastFetchedPersonaId.current = consumerPersonaId;

    const getHoldingProxies = async () => {
      try {
        const holdings: DTHoldingProxy[] | Error = await getAllHoldingProxies();
        if (holdings instanceof Error) {
          setUserHoldings([]);
        } else {
          // Check each holding for missing serial number
          for (const holding of holdings) {
            const isRequired = await checkSerialNumberRequired(holding);
            if (isRequired) {
              setSerialRequiredProduct(holding);
              break; // Show modal for first incomplete registration
            }
          }
          setUserHoldings(fixCategoryAndSortHoldingsByTCreate(holdings));
        }
        setIsLoading(false);
      } catch (innerErr) {
        console.error('Error fetching holdings:', innerErr);
        setIsLoading(false);
      }
    };

    getHoldingProxies().catch((err) => {
      console.error('Error in getHoldingProxies:', err);
      setIsLoading(false);
    });
    // Note: getAllHoldingProxies and checkSerialNumberRequired are intentionally excluded from deps
    // to prevent infinite loops - they are recreated on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumerPersonaId]);

  return (
    <div className="lg:mb-10">
      <div className="flex items-center justify-between">
        <Heading kind={'h3'}>
          Hi{' '}
          {consumerPersonaData?.splitname?.first ||
            consumerPersonaData?.fullname}
          , good {getGreeting()}.
        </Heading>
      </div>
      {openCaseIds?.length > 0 && (
        <div className="mt-6 md:mt-10">
          <HomeTitleWithCTA
            hasTitle={true}
            hasSubtitle={false}
            title={'Repairs in progress'}
          />
          <div className="mt-4 md:mt-7">
            <div className="grid grid-cols-1 gap-5 md:grid md:grid-cols-2">
              {getCases()}
            </div>
          </div>
        </div>
      )}
      <div className="mt-7">
        <HomeTitleWithCTA
          hasTitle={true}
          hasSubtitle={false}
          title={'Recent uploads'}
          onClick={() => navigate(ROUTE_NAME.PRODUCTS_SEARCH)}
        />
        <div className="mt-5">
          {isLoading && (
            <div className="grid grid-cols-1 gap-5 md:grid md:grid-cols-2">
              <Skeleton count={1} className="min-h-[128px] w-full" />
              <Skeleton count={1} className="min-h-[128px] w-full" />
            </div>
          )}
          {userHoldings.length === 0 && !isLoading && (
            <div className="mx-auto w-full sm:w-2/3 md:w-1/2">
              <EmptyListCTA
                copy={'No products yet! Click the button above to get started.'}
                imgSrc={noProductsImg}
                imgAlt={'emptyList'}
              />
            </div>
          )}
          {userHoldings.length > 0 && (
            <div className="grid grid-cols-1 gap-5 md:grid md:grid-cols-2">
              {userHoldings.slice(0, 4).map((holding) => (
                <RenderHoldingCard
                  key={holding.docid}
                  holding={holding}
                  checkSerialNumberRequired={checkSerialNumberRequired}
                />
              ))}
            </div>
          )}
          {/* <LayoutGrid kind={'uploads'} items={userHoldings}></LayoutGrid> */}
        </div>
      </div>
      {!config.DASHBOARD.OLIVE.HIDE_MOMENT_BANNER && (
        <div className="mt-8">
          <HomeTitleWithCTA
            hasTitle={true}
            hasSubtitle={false}
            title={'Message of the moment'}
          />
          <div className="mt-5">
            <CardImageBg
              dailyMsg={true}
              message={
                tenantInfo?.messageOfTheMoment
              }
              imageUrl={bg_motd}
            />
          </div>
        </div>
      )}
      {/* Keep the Shelta string hardcoded for exceptions - VP */}
      {tenantInfo?.name === 'Shelta Australia ' && (
        <>
          <div className="mt-8">
            <HomeTitleWithCTA
              hasTitle={true}
              hasSubtitle={true}
              title={'Suggestions'}
            />
            <div className="mt-4">
              <LayoutGrid kind={'suggestions'} />
            </div>
          </div>
          <div className="mt-8">
            <HomeTitleWithCTA
              hasTitle={false}
              hasSubtitle={true}
              subTitle={'Other amazing suggestions'}
            />
            <div className="mt-4 lg:mt-8">
              <LayoutGrid kind={'otherSuggestions'} />
            </div>
          </div>
        </>
      )}
      {serialRequiredProduct && (
        <Modal
          className="lg:w-[560px]"
          kind={'generic'}
          settings={{
            title: t('Complete registration'),
            handleClose() {
              setSerialRequiredProduct(null);
            },
          }}
        >
          <div className="flex flex-col gap-4 p-4 text-center">
            <div className="font-lg font-caudex text-left font-medium text-gray-600">
              {t(
                "Your product registration isn't complete yet. Please finish registering to activate your warranty."
              )}
            </div>
            <RenderHoldingCard
              holding={serialRequiredProduct}
              checkSerialNumberRequired={checkSerialNumberRequired}
            />
            <Button
              kind={'primary'}
              onClick={() =>
                navigate(
                  ROUTE_NAME.PRODUCTS_PREVIEW +
                    '/' +
                    serialRequiredProduct.vendable +
                    '/register?holdingId=' +
                    serialRequiredProduct.holdingLink
                )
              }
            >
              {t('Complete registration')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const RenderHoldingCard = ({
  holding,
  checkSerialNumberRequired,
}: {
  holding: DTHoldingProxy;
  checkSerialNumberRequired: (holding: DTHoldingProxy) => Promise<boolean>;
}) => {
  const navigate = useNavigate();
  const { tenantInfo, config } = useContext(AppContext);
  const [serialNumberRequired, setSerialNumberRequired] = useState(false);

  useEffect(() => {
    const updateSerialRequired = async () => {
      const isRequired = await checkSerialNumberRequired(holding);
      setSerialNumberRequired(isRequired);
    };
    updateSerialRequired();
  }, [holding, config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED]);

  if (holding.deleted) return null;
  const productInfo = holding?.self_registration?.srvInfo
    ?.productInfo as TableCover;
  const imageUrl = productInfo?.typeofStone
    ? getStoneImages(productInfo?.typeofStone)
    : holding.img?.[0] || holding.self_registration?.img?.[0] || '';
  const warrantyInfo = BL_Warranty.getLatestWarranty(holding?.warranties);
  const isVoided = warrantyInfo?.status === WarrantyStatus.VOIDED;
  
  return (
    <div
      key={holding.docid.split('_')[0]}
      className="relative rounded-md border border-gray-200"
    >
      {isVoided && (
        <div className="absolute top-1/2 left-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2">
          <VoidBadgeIcon className="h-[60px] w-[200px] -rotate-45 text-red-600 opacity-[0.15]" />
        </div>
      )}
      <CardGeneral
        onClick={() =>
          navigate(
            `${ROUTE_NAME.PRODUCTS}/${holding.docid.split('_')[0]}/details`
          )
        }
        holdingData={holding}
        imageUrl={imageUrl}
        tenantInfo={tenantInfo}
        serialNumberRequired={serialNumberRequired}
      />
    </div>
  );
};

export default DashboardView;
